import React, {
  type FormEventHandler,
  type JSX,
  useEffect, useRef, useState
} from "react";
import type { SignupSubscriptionComponentProps } from "./SignupSubscriptionComponentProps.d";

const ACCEPT_APPLICATION_JSON = "application/json";

const CONTENT_TYPE_APPLICATION_JSON_UTF8 = "application/json;charset=UTF-8";

function validateEmail(email: string) {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}

function SignupSubscription({
  contentId,
  enableSubscriptions,
  serviceUrl,
  showMargin,
  subscriptionTitle,
  title
}: SignupSubscriptionComponentProps): JSX.Element {
  const emailId = `email${contentId}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const signupRef = useRef<HTMLDivElement>(null);

  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [hasContainerClass, setHasContainerClass] = useState(true);

  useEffect(() => {
    // Check if the signup element has a parent with class 'l-container'
    if (signupRef.current) {
      const parent = signupRef.current.parentElement;
      const hasParentWithClass = parent?.closest(".l-container") !== null;
      if (hasParentWithClass) {
        setHasContainerClass(false); // Remove the class by updating state
      }
    }
  }, []); // Empty dependency array to run once on mount

  const onSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    if (inputRef.current) {
      const email = inputRef.current.value;
      const valid = validateEmail(email);
      if (valid) {
        if (enableSubscriptions) {
          fetch(`${serviceUrl}&email=${email}`, {
            headers: {
              Accept: ACCEPT_APPLICATION_JSON,
              "Content-Type": CONTENT_TYPE_APPLICATION_JSON_UTF8
            },
            method: "GET"
          }).then((res) => {
            if (res.status === 200) {
              setShowSuccess(true);
            } else {
              setShowError(true);
            }
          });
        } else {
          console.log("subscriptions are disabled");
        }
      } else {
        setShowError(true);
      }
    }
  };

  return (
    <div
      className={`${hasContainerClass ? "l-container" : ""} l-mt-4${showMargin ? " l-mb-5" : ""}`}
      id="signup"
      ref={signupRef}
    >
      <section className="b-box b-box--square b-box--blue">
        <form
          className={`row ${showSuccess ? "hidden" : ""}`}
          id={`subscribe${contentId}`}
          onSubmit={onSubmit}
        >
          <div className="col-md-5 col-xl-4">
            <h2 className="h3">{title}</h2>
            <label htmlFor={emailId} className="b-input-text__label">
              Fyll inn e-postadressen din for å motta varsler om:
              {" "}
              <br />
              <strong>{subscriptionTitle}</strong>
            </label>
          </div>
          <div className="col-md-6 l-mb-1">
            <div className="l-layout b-input-text">
              <input
                className="b-input-text__input b-input-text__input--mt-1 col-md-7 l-mt-1 l-mr-1"
                id={emailId}
                ref={inputRef}
                type="text"
              />
              <div className="l-mt-1">
                <button className="b-button" type="submit">Abonnér</button>
              </div>
            </div>
            <div id={`error${contentId}`} className={`b-input-text__error ${showError ? "" : "hidden"}`}>
              Skriv inn en gyldig epost-adresse i dette formatet: dittnavn@eksempel.com
            </div>
          </div>
        </form>
        <div
          aria-atomic="true"
          aria-live="polite"
          className={`b-input-text__description l-mt-1 ${showSuccess ? "" : "hidden"}`}
          id={`success${contentId}`}
        >
          <h2 className="h3">Bekreftelse</h2>
          Abonnementet ditt er registrert. Du får straks e-post fra oss.
        </div>
      </section>
    </div>
  );
}

export default function(props: SignupSubscriptionComponentProps): JSX.Element {
  return <SignupSubscription {...props} />;
}
